<template>
  <div
    class="doctor-schedule h-full flex flex-col"
    :class="{ 'doctor-schedule--popup': isPopup }"
  >
    <ErrorModal
      :visible="errorModalVisible"
      :messages="errorModalMessages"
      :fallback-message="
        $t(
          'The selected time slot is no longer available. Please choose another time.',
        )
      "
      :title="$t('We couldn\'t complete your booking')"
      :cta-label="$t('Select another time slot')"
      @close="closeErrorModal"
    />

    <div v-if="activeBookingOption">
      <div>
        <ul
          v-if="displayCenters.length > 1"
          class="flex border rounded-[10rem] border-solid border-[rgba(0,0,0,0.06)] address_list overflow-x-auto whitespace-nowrap"
        >
          <li
            class="sm:min-w-[100px] w-3/12 border-r-[rgba(0,0,0,0.06)] border-r border-solid shrink-0"
            v-for="center in displayCenters"
            :key="center.key"
          >
            <a
              href="#"
              :class="{ active: activeCenterKey === center.key }"
              class="block text-black font-medium text-center px-0 py-[0.8rem] flex items-center justify-center"
              @click.prevent="setActiveCenter(center.key)"
            >
              {{ center.name }}
            </a>
          </li>
        </ul>

        <div
          v-if="activeLocationLabel"
          class="doctor-schedule__location flex items-center justify-start gap-2 px-0 py-[0.6rem]"
          :class="{ 'sm:hidden': isPopup }"
        >
          <img
            class="w-[1.6rem]"
            :src="activeLocationIcon"
            :alt="activeLocationIconAlt"
          />
          <p class="text-black text-[1.2rem] font-medium">
            {{ activeLocationLabel }}
          </p>
        </div>
      </div>
      <div class="select_doctor_schedule" :key="scheduleRenderKey">
        <h2
          class="doctor-schedule__title text-black font-semibold"
          :class="[
            serviceTitle ? 'text-[1.5rem] text-left mb-1' : 'text-[1.4rem] text-center',
            { 'sm:hidden': isPopup && !serviceTitle },
          ]"
        >
          {{ serviceTitle || $t("Select schedule") }}
        </h2>

        <div class="w-full">
          <DoctorScheduleDateSlider
            :dates="currentDates"
            :active-index="activeDateIndex"
            :loading="scheduleLoading"
            :disable-prev="disablePrev"
            :disable-next="scheduleLoading || !totalDateGroups"
            @prev="prevDateGroup"
            @next="nextDateGroup"
            @select="
              (index) => {
                setActiveDate(index);
                if (compact) $emit('expand');
              }
            "
          />
        </div>
      </div>
      <DoctorScheduleTimeGrid
        v-if="!compact"
        :columns="scheduleColumns"
        :visible-rows="visibleSchedules"
        :column-count="scheduleColumnCount"
        :loading="scheduleLoading"
        :booking-in-progress="bookingInProgress"
        :can-load-more="visibleSchedules < totalTimesMax"
        :can-show-less="visibleSchedules > baseVisibleSchedules"
        :is-selectable-time="isSelectableTime"
        :is-button-loading="isButtonLoading"
        @select-time="selectTime"
        @load-more="loadMoreSchedules"
        @show-less="showLessSchedules"
      />
      <button
        v-else
        type="button"
        class="doctor-schedule__expand"
        @click="$emit('expand')"
      >
        {{ $t("See available times") }}
      </button>
    </div>
    <div
      v-else
      class="flex-1 flex items-center justify-center text-center text-[1.2rem] text-[#8B8B8B]"
    >
      {{
        hasSelectedBooking
          ? $t("No availability for this specialist")
          : $t("Select a service type")
      }}
    </div>

    <DoctorScheduleDurationSelector
      :visible="durationSelector.visible"
      :options="durationSelector.options"
      :date="durationSelector.date"
      :time="durationSelector.time"
      :loading="durationSelector.loading"
      :prices="durationSelector.prices"
      :slot-info="durationSelector.slotInfo"
      :selected-duration="durationSelector.selectedDuration"
      :deposit-display="activeBookingOption?.depositDisplay || null"
      title="Confirm your reservation"
      confirm-label="Confirm and continue"
      @select="selectDurationOption"
      @confirm="confirmPrebookSelection"
      @close="closeDurationSelector"
    />
  </div>
</template>

<script>
import doctorScheduleConfig from "./doctorSchedule.logic";

export default doctorScheduleConfig;
</script>

<style scoped>
.active,
.address_list li a:hover {
  @apply text-white bg-[#464646] shadow-[inset_0_0.4rem_0.4rem_rgba(0,0,0,0.70)];
}

.address_list li:first-child a {
  border-radius: 10rem 0 0 10rem;
}

.address_list li:last-child a {
  border-radius: 0 10rem 10rem 0;
}

.address_list li a:hover img,
.active img {
  filter: brightness(0) saturate(100%) invert(96%) sepia(99%) saturate(1%)
    hue-rotate(141deg) brightness(105%) contrast(100%);
}

.activeDate {
  color: black;
}

.doctor-schedule__expand {
  width: 100%;
  margin-top: 1.6rem;
  min-height: 4.4rem;
  padding: 0.8rem 1.6rem;
  border-radius: 999px;
  border: 1px solid #5b6fee;
  background: rgba(91, 111, 238, 0.08);
  color: #3346d3;
  font-size: 1.3rem;
  font-weight: 600;
  transition: background 0.18s ease, transform 0.12s ease;
}

.doctor-schedule__expand:hover {
  background: rgba(91, 111, 238, 0.14);
}

.doctor-schedule__expand:active {
  transform: translateY(1px);
}

.booking-option {
  transition: background-color 0.2s ease, color 0.2s ease,
    border-color 0.2s ease;
}

.booking-option--active {
  @apply text-white;
  background: #464646;
  border-color: #464646;
  box-shadow: inset 0px 0.4rem 0.4rem rgba(0, 0, 0, 0.7);
}

@media (max-width: 767px) {
  .doctor-schedule--popup .doctor-schedule__title,
  .doctor-schedule--popup .doctor-schedule__location {
    display: none;
  }
}
</style>
